import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for integer validation
 */
export interface IntegerValidatorOptions extends BaseValidatorOptions {
  min?: number;
  max?: number;
}

/**
 * Validate an integer value (whole numbers only, no decimals)
 * @param value - The string representation of an integer
 * @param options - Validation options (min, max)
 * @returns Validation result with the parsed integer value and optional error, or null if invalid
 *
 * @example
 * ```typescript
 * const result = validateInteger("42", { min: 0, max: 100 });
 * if (result) {
 *   console.log(result.value); // 42
 * }
 *
 * const invalid = validateInteger("42.5"); // Returns null (decimals not allowed)
 * ```
 */
export function validateInteger(value: string, options: IntegerValidatorOptions = {}): ValidationResult<number> | null {
  const { field = "value", min, max } = options;

  // Check if value contains a decimal point
  if (value.includes(".")) {
    return null;
  }

  const intValue = parseInt(value, 10);
  if (isNaN(intValue)) {
    return null;
  }

  if (min !== undefined && intValue < min) {
    return {
      value: intValue,
      error: {
        field,
        message: `"${field}" must be at least ${min} (got ${intValue})`,
        severity: "warning",
      },
    };
  }

  if (max !== undefined && intValue > max) {
    return {
      value: intValue,
      error: {
        field,
        message: `"${field}" must be at most ${max} (got ${intValue})`,
        severity: "warning",
      },
    };
  }

  return { value: intValue };
}
